import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import {
  DataGrid,
  type InternalProps,
  type DataTableProps,
  type DataGridState,
  type ColGroupDef,
  type ColDef,
} from "modules/datatable/DataGrid"
import { type MartPreset } from "modules/datatable/types"
import { useMetricsMeta } from "modules/metrics/hooks/useMeta"
import { type ComponentType, useMemo } from "react"
import { type MartColumn } from "types/ApiTypes"
import { type MartData, createDatasource } from "./datasource"
import { resolveColDefs } from "./martColumnUtils"
import type { GridOptions as AgGridOptions } from "@ag-grid-community/core"
type MartDataGridProps<T extends MartData> = {
  apiRef?: React.RefObject<AgGridOptions | null>
  martId: string
  height?: string | number
  initialData?: Array<T>
  columns: Array<MartColumn>
  defaultPreset?: null | MartPreset
  customPreset?: null | MartPreset
  initialSideBar?: DataTableProps<any>["initialSideBar"]
  component?: ComponentType<DataTableProps<T, {}> & InternalProps>
  onStateChange?: (state: DataGridState) => void
  readOnly?: boolean
  disableCharts?: boolean
}

function augmentColWithReadonlyProps(col: ColDef<any, any>): ColDef<any, any> {
  return {
    ...col,
    filterParams: {
      ...col.filterParams,
    },
    enableRowGroup: false,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    floatingFilter: false,
    floatingFilterComponent: null,
  }
}

const readOnlyProps: DataTableProps<any> = {
  sideBar: false,
  rowGroupPanelSuppressSort: true,
  suppressRowDrag: true,
  functionsReadOnly: true,
  suppressRowGroupHidesColumns: true,
  suppressDragLeaveHidesColumns: true,
  suppressGroupHeader: true,
  autoGroupColumnDef: augmentColWithReadonlyProps({}),
}

export const MartDataGrid = <T extends MartData>({
  apiRef,
  martId,
  height,
  initialData = [],
  columns,
  defaultPreset,
  customPreset,
  initialSideBar,
  onStateChange,
  component: Component = DataGrid,
  readOnly,
  disableCharts,
}: MartDataGridProps<T>) => {
  const metricConfigurations = useMetricsMeta()

  const datasource = useMemo(
    () => createDatasource(martId, initialData),
    [martId, initialData]
  )

  const columnDefinitionsByColId = toDictionary(columns, "col_id")

  const collapsedColumnDefinitions = useMemo(() => {
    return columns.reduce((acc, col) => {
      if (!col.parent) {
        // @ts-ignore
        acc.push(col)
        return acc
      } else {
        const parentColumnDefinition = columnDefinitionsByColId.get(
          col?.parent || ""
        )

        acc.push({
          ...col,
          // @ts-ignore
          params: {
            header: parentColumnDefinition?.header_name,
          },
          parent: null,
          header_name: col.header_name,
        })
      }

      return acc
    }, [] as MartColumn[])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns])

  const columnDefs = useMemo(
    () =>
      resolveColDefs(
        collapsedColumnDefinitions,
        martId,
        defaultPreset,
        metricConfigurations
      ),
    [collapsedColumnDefinitions, martId, defaultPreset, metricConfigurations]
  )

  const augmentedColumnDefs = useMemo(() => {
    if (!readOnly) {
      return columnDefs
    }

    return columnDefs
      .map((col) => {
        if ((col as ColGroupDef<any>).children) {
          ;(col as ColGroupDef<any>).children = (
            col as ColGroupDef<any>
          ).children.map((child: ColDef<any, any>) => {
            return augmentColWithReadonlyProps(child)
          })
        } else {
          col = augmentColWithReadonlyProps(col)
        }

        return col
      })
      .filter((col) => {
        if ((col as ColGroupDef<any>).children) {
          return false
        }

        return true
      })
  }, [columnDefs, readOnly])

  return (
    <Component
      apiRef={apiRef}
      datasource={datasource}
      columnDefs={augmentedColumnDefs}
      preset={customPreset}
      height={height}
      initialFilter={defaultPreset?.config?.filters}
      initialSideBar={initialSideBar}
      enableCharts={!disableCharts}
      onStateChange={onStateChange}
      {...(readOnly ? readOnlyProps : {})}
    />
  )
}
